import React, { useContext, useState } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { LandUseChange, LandUseChangeRow } from '../../../../../../services/types/Calculator';
import { DropDown, Button, ConfirmModal, Modal } from '../../../../../../shared/components';
import LandUseChangeDetails from './Details/LandUseChangeDetails';
import MainContext from '../../../../../App/MainProvider/context';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface LandUseChangeBaseProps {
  landUseChange: LandUseChange;
  onUpdateLandUseChange: (updatedLandUseChange: LandUseChange) => void;
  calculateFunction: (aboveGBCarbon: number, belowGBCarbon: number, area: number) => number;
  calculateSubtotal: (rows: LandUseChangeRow[]) => number | null;
}

const LandUseChangeBase: React.FC<LandUseChangeBaseProps> = ({
  landUseChange,
  onUpdateLandUseChange,
  calculateFunction,
  calculateSubtotal,
}) => {
  const { t } = useTranslation();

  const landUseChangeOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(landUseChange.coversType
      ? landUseChange.coversType.map((x) => ({
          id: x.id,
          text: x.name,
          value: x.id.toString(),
        }))
      : []),
  ];

  const { notify } = useContext(MainContext);
  const [selectedValueLandUseChangeOption, setSelectedValue] = useState<number | null>(0);
  const [landUseInputValue, setLandUseInputValue] = useState<string>('');
  const [rows, setRows] = useState<LandUseChangeRow[]>(landUseChange.rows || []);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [rowForDelete, setRowForDelete] = useState<number | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const calculatedSubtotal = calculateSubtotal(rows);

  const isAddButtonDisabled =
    selectedValueLandUseChangeOption === null ||
    selectedValueLandUseChangeOption === 0 ||
    Number(landUseInputValue) <= 0;

  const handleChange = (value: number) => {
    setSelectedValue(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setLandUseInputValue(value);
    }
  };

  const isDuplicateCoverType = (existingRows: LandUseChangeRow[], coverId: number) => {
    const isDuplicate = existingRows.some((row) => row.coverId === coverId);

    if (isDuplicate) {
      notify(
        'Land Use Change',
        'A row with this Land Cover Type already exists.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  const handleAddRow = () => {
    if (selectedValueLandUseChangeOption !== null && Number(landUseInputValue) > 0) {
      const selectedCoverType = landUseChange.coversType.find(
        (x) => x.id === selectedValueLandUseChangeOption
      );

      if (selectedCoverType) {
        if (isDuplicateCoverType(rows, selectedCoverType.id)) {
          return;
        }

        const calculatedAreaCleared = calculateFunction(
          selectedCoverType.aboveGBCarbon,
          selectedCoverType.belowGBCarbon,
          Number(landUseInputValue)
        );

        const newRow = {
          id: 0,
          coverId: selectedCoverType.id,
          coverType: selectedCoverType.name,
          area: Number(landUseInputValue),
          totalCarbon: calculatedAreaCleared,
          aboveGBCarbon: selectedCoverType.aboveGBCarbon,
          belowGBCarbon: selectedCoverType.belowGBCarbon,
        };

        setRows((prevRows) => {
          const updatedRows = [...prevRows, newRow];
          const newSubtotal = calculateSubtotal(updatedRows);
          onUpdateLandUseChange({
            ...landUseChange,
            rows: updatedRows,
            subTotal: newSubtotal,
          });
          return updatedRows;
        });

        setLandUseInputValue('');
        setSelectedValue(0);
      }
    }
  };

  const confirmRemoveRow = () => {
    if (rowForDelete !== null) {
      setRows((prevRows) => {
        const updatedRows = prevRows.filter((_, i) => i !== rowForDelete);
        const newSubtotal = updatedRows.length > 0 ? calculateSubtotal(updatedRows) : null;
        onUpdateLandUseChange({
          ...landUseChange,
          rows: updatedRows,
          subTotal: newSubtotal,
        });
        return updatedRows;
      });
      setShowConfirmDelete(false);
      setRowForDelete(null);
    }
  };

  const handleRemoveRow = (index: number) => {
    setRowForDelete(index);
    setShowConfirmDelete(true);
  };

  return (
    <>
      <h4 className="table-calculator-title">{t('Select Parameters')}</h4>
      <div className="form-row align-items-center calculator-forms table-wrapper-calc">
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Select Land Cover Type')}
          </label>
        </div>
        <div className="col-sm-3 d-flex align-items-center">
          <DropDown
            elements={landUseChangeOptions}
            value={selectedValueLandUseChangeOption}
            onChange={handleChange}
            key="drop-landUseChange"
            className="dropdown-module-calculator"
          />
        </div>
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Area Cleared (ha)')}
          </label>
        </div>

        <div className="col-sm-2 d-flex align-items-center">
          <input
            title="Area Cleared (ha)"
            type="number"
            id="landUseInput"
            value={landUseInputValue}
            onChange={handleInputChange}
            min="0"
            max={99999999.99}
            maxLength={10}
            onKeyDown={handleKeyDown}
            className="calculator-input"
          />
        </div>
        <div className="col-sm-3">
          <Button
            htmlId="addRowLandUse"
            className="btn btn-primary px-4 py-1 calculator-add-button"
            style={{ float: 'right' }}
            text={t('Add Row')}
            onClick={handleAddRow}
            disabled={isAddButtonDisabled}
          />
        </div>
      </div>

      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered action-padding-calculator">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Land Cover Type</th>
              <th>Area Cleared</th>
              <th className="text-right">Subtotal</th>
              <th className="action-column text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id}>
                <td>{row.coverType}</td>
                <td>
                  <NumberFormatterSinSeparador value={row.area} />
                </td>
                <td className="text-right">
                  <NumberFormatterSinSeparador value={row.totalCarbon * (44 / 12)} /> t CO
                  <sub>2</sub>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId={`remove-row-landuse-${index}`}
                    className="btn-tertiary no-padding action-calculator"
                    icon="trash-alt"
                    text=""
                    onClick={() => handleRemoveRow(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={3}>
                  <div>
                    Subtotal: <NumberFormatterSinSeparador value={calculatedSubtotal} /> t CO
                    <sub>2</sub>
                  </div>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId={`tr-landUselink-detail-${landUseChange.moduleId}`}
                    className="btn-tertiary no-padding action-calculator"
                    icon="clipboard-list"
                    text=""
                    onClick={() => setShowDetailsModal(true)}
                  />

                  <UncontrolledTooltip
                    className="tooltip-toolkit"
                    placement="bottom"
                    target={`tr-landUselink-detail-${landUseChange.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('Details')}
                  </UncontrolledTooltip>
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
      {showConfirmDelete && rowForDelete !== null && (
        <ConfirmModal
          visible={showConfirmDelete}
          buttonText={t('Confirm')}
          setVisible={setShowConfirmDelete}
          action={confirmRemoveRow}
        >
          {t('confirmDeleteCalculateMessage')}
        </ConfirmModal>
      )}
      <Modal
        title={landUseChange.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal calculator"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <LandUseChangeDetails landUseChange={landUseChange} />
        </div>
      </Modal>
    </>
  );
};

export default LandUseChangeBase;
